import Link from "next/link"
import Image from "next/image"
import type { ReactNode } from "react"
import { ArrowRight, Calendar, Clock } from "lucide-react"
import { getCategoryColor } from "@/lib/category-utils"
import { getDirectusImageUrl } from "@/lib/directus"
import { generateBlurDataURL } from "@/lib/image-utils"
import { getCategoryIcon } from "./blog-category-icon"

interface BlogGlassPost {
  id: string
  title: string
  excerpt: string
  category: string
  date?: string
  date_published?: string
  image: string
  featured?: boolean
  slug?: string
  reading_time?: number
}

function fmtDate(date?: string): string {
  if (!date) return ""
  try {
    // timeZone: "UTC" pins the format so SSR (server runs in UTC) and CSR (the
    // visitor's local TZ) produce identical text. Without it, a post published
    // near UTC midnight renders a different calendar day on the client than the
    // ISR-cached server HTML, tripping a React #418 hydration mismatch. This
    // card is rendered inside a client component (ResponsiveBlogCards), so the
    // mismatch is live — same fix already applied in blog-card.tsx + formatBlogDate.
    return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
  } catch {
    return ""
  }
}

// Wrap case-insensitive matches of `term` in <mark>, returning React nodes
// (no dangerouslySetInnerHTML). Mirrors the search-highlight in BlogCard.
function highlight(text: string, term?: string): ReactNode {
  const t = term?.trim()
  if (!t) return text
  const safe = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const parts = text.split(new RegExp(`(${safe})`, "ig"))
  return parts.map((part, i) =>
    part.toLowerCase() === t.toLowerCase() ? (
      <mark key={i} className="rounded-[3px] bg-amber-300/40 px-px text-inherit">
        {part}
      </mark>
    ) : (
      part
    ),
  )
}

export function BlogGlassCard({
  post,
  searchTerm,
  imagePriority,
}: {
  post: BlogGlassPost
  searchTerm?: string
  imagePriority?: boolean
}) {
  const color = getCategoryColor(post.category)
  const Icon = getCategoryIcon(post.category)
  const date = post.date_published || post.date

  return (
    <Link
      href={`/blog/${post.slug || post.id}`}
      className="ds-glass group relative flex h-full flex-row md:flex-col overflow-hidden rounded-[18px] no-underline transition duration-300 ease-out hover:-translate-y-1 focus-visible:outline-2 focus-visible:outline-green-500 focus-visible:outline-offset-2"
    >
      {/* Category-tinted media tile: real image, else a category icon */}
      <div
        className="relative aspect-square w-[128px] flex-none overflow-hidden md:aspect-[16/9] md:w-full"
        style={{ background: `linear-gradient(150deg, color-mix(in oklch, ${color} 16%, #ffffff), #ffffff 78%)` }}
      >
        {post.image ? (
          <Image
            src={getDirectusImageUrl(post.image, 400, 225)}
            alt={post.title}
            fill
            sizes="(max-width: 768px) 128px, (max-width: 1200px) 50vw, 400px"
            placeholder="blur"
            blurDataURL={generateBlurDataURL(post.image)}
            className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            priority={imagePriority}
          />
        ) : (
          <span className="absolute inset-0 grid place-items-center" style={{ color, opacity: 0.5 }}>
            <Icon size={44} strokeWidth={1.5} />
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col justify-center gap-1.5 p-3.5 md:justify-start md:gap-2.5 md:p-[18px]">
        <span className="inline-flex items-center gap-1.5 text-[0.78rem] font-semibold text-[#41514a]">
          <span className="h-2 w-2 flex-none rounded-full" style={{ background: color }} />
          {post.category}
        </span>
        <h3 className="m-0 text-[15px] md:text-[1.18rem] font-extrabold leading-snug md:leading-[1.22] tracking-[-0.018em] text-[#14241b] transition-colors group-hover:text-green-700 line-clamp-2 md:line-clamp-none">
          {highlight(post.title, searchTerm)}
        </h3>
        <div className="flex flex-wrap items-center gap-2 text-[0.78rem] text-[#6b7a73]">
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={13} className="text-gray-400" />
            {fmtDate(date)}
          </span>
          <span className="text-gray-300">•</span>
          <span className="inline-flex items-center gap-1.5">
            <Clock size={13} className="text-gray-400" />
            {post.reading_time || 5} min read
          </span>
        </div>
        <p className="m-0 hidden text-[0.9rem] leading-[1.55] text-[#41514a] md:line-clamp-3 md:flex-1">
          {highlight(post.excerpt, searchTerm)}
        </p>
        <span className="mt-0.5 hidden items-center gap-1.5 text-[0.9rem] font-semibold text-green-700 transition-all duration-200 sm:inline-flex sm:opacity-0 sm:-translate-x-1 sm:group-hover:translate-x-0 sm:group-hover:opacity-100">
          Read article
          <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  )
}
